import { APIFactory } from 'ui-toolkit/rest/APIFactory';
import { axiosErrorHandler } from 'ui-toolkit/rest/axiosErrorHandler';
import { IPolicyJacketUI } from '../classes/PolicyJacketUI';
import { PolicyTypeEnum } from '../classes/PolicyTypeEnum';
import { IRecordedDeed } from '../classes/RecordedDeed';
import { IRecordedMortgage } from '../classes/RecordedMortgage';
import getConfig from '../utils/getConfig';
import { baseUrlService } from '../../ui-toolkit/rest/BaseUrlService';

export const getOrderDetailsAsync = (policyType: PolicyTypeEnum, orderId: string) =>
  APIFactory(baseUrlService.ApiLocation)
    .get(
      `/timber/api/internal/orders/${orderId}/document-categories/${getPolicyDocumentCategory(policyType)}/pages/schedule-a`
    )
    .then((response) => response.data)
    .catch(axiosErrorHandler);

const getPolicyDocumentCategory = (policyType: PolicyTypeEnum): string => {
  switch (policyType) {
    case PolicyTypeEnum.shortForm:
      return 'policy-short-form-loan-policy-prepared';
    case PolicyTypeEnum.longForm:
      return 'policy-loan-policy-jacket-jacket';
    case PolicyTypeEnum.owner:
    default:
      return 'policy-owners-policy-jacket-jacket';
  }
};

export const uploadRecordedDeedAsync = async (policyId: string, recordedDeed: IRecordedDeed): Promise<any> => {
  try {
    return await APIFactory(baseUrlService.ApiLocation).post(`/policies/v2/recorded_deed/${policyId}`, recordedDeed);
  } catch (e: any) {
    axiosErrorHandler(e);
  }
};

export const uploadRecordedMortgageAsync = async (
  policyId: string,
  recordedMortgage: IRecordedMortgage
): Promise<any> => {
  try {
    return await APIFactory(baseUrlService.ApiLocation).post(
      `/policies/v2/recorded_mortgage/${policyId}`,
      recordedMortgage
    );
  } catch (e: any) {
    axiosErrorHandler(e);
  }
};

export const deletePolicyJacketAsync = async (policyType: PolicyTypeEnum, orderId: string): Promise<any> => {
  try {
    return policyType === PolicyTypeEnum.shortForm
      ? await APIFactory(baseUrlService.ApiLocation).delete(
          `/documents/v4/${orderId}/${getPolicyType(policyType)}/versioned/delete`
        )
      : await APIFactory(baseUrlService.ApiLocation).delete(
          `/policies/v2/${getPolicyType(policyType)}/policy_jacket/${orderId}`
        );
  } catch (e: any) {
    axiosErrorHandler(e);
  }
};

export const uploadPolicyJacketAsync = async (
  policyJacketFile: File,
  policyJacket: IPolicyJacketUI,
  orderId: string
): Promise<any> => {
  const formData = new FormData();
  formData.append('file', policyJacketFile);
  if (getConfig(`config.policy.policyNumberRequired.${policyJacket.policyCategory}`)) {
    formData.append(
      'json',
      JSON.stringify({
        policyCategory: getPolicyType(policyJacket.policyCategory),
        policyNumber: policyJacket.policyNumber,
      })
    );
  }

  try {
    return await APIFactory(baseUrlService.ApiLocation).post(
      policyJacket.policyCategory === PolicyTypeEnum.shortForm
        ? `/documents/v4/${orderId}/${getPolicyType(policyJacket.policyCategory)}/versioned/upload`
        : `/policies/v2/upload/policy_jacket/${orderId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  } catch (e: any) {
    axiosErrorHandler(e);
  }
};

export const updatePolicyNumberAsync = async (policyJacket: IPolicyJacketUI, orderId: string): Promise<any> => {
  const formData = {
    policyCategory: getPolicyType(policyJacket.policyCategory),
    policyNumber: policyJacket.policyNumber,
  };
  try {
    return await APIFactory(baseUrlService.ApiLocation).post(`/policies/v2/policy_jacket/${orderId}`, formData);
  } catch (e: any) {
    e.orderIdWithExistingPolicy ? await Promise.reject(e) : axiosErrorHandler(e);
  }
};

const getPolicyType = (policyType: PolicyTypeEnum | null): string => {
  let type: string;
  switch (policyType) {
    case PolicyTypeEnum.shortForm:
      type = 'ShortFormLendersPolicy';
      break;
    case PolicyTypeEnum.longForm:
      type = 'loan';
      break;
    default:
      type = 'owner';
      break;
  }
  return type;
};
